// Org settings — the organisation profile, comfort modes, language and the
// signed-in account.
//
// WHAT WAS WRONG BEFORE. Every fact on this screen was a fixture or a lie:
//   • the name/type fields were seeded from `domain/fixtures.organisation`, so a
//     real admin saw "Bright Steps Centre" whatever their org was actually called;
//   • both Save buttons were `setState(true)` — they showed "Profile saved." and
//     wrote nothing, anywhere, ever;
//   • "Signed in as" was hardcoded to `marcus@brightsteps.org`, i.e. ANOTHER
//     PERSON'S email address rendered as your own identity;
//   • the Sign out button had no onClick at all.
//
// Now: the profile is seeded from `useOrg()` (resolved once by the admin gate),
// Save calls `updateOrganisation` and only confirms after the promise resolves,
// the identity comes from the Outlet context the shell supplies, and sign-out is
// the shell's real sign-out. Data residency has no backing store, so it no
// longer pretends to save — it says so plainly instead.

import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { getDictionary, LOCALE_LABEL, LOCALES } from "../../i18n/dictionary";
import { getPortalData, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { useOrg } from "./OrgContext";
import type { OrganisationType } from "../../domain/types";

// ---- Local strings ----------------------------------------------------------

const L = {
  en: {
    profileTitle: "Organisation profile",
    nameLabel: "Organisation name",
    typeLabel: "Organisation type",
    typeCentre: "Early childhood centre",
    typeClinic: "Clinic",
    typeSchool: "School",
    typeOther: "Other",
    profileSaved: "Profile saved.",
    saving: "Saving…",
    residencyNotConfigurable:
      "This isn't something you can set from the portal yet. Your actual data residency is determined by your hosting arrangement with us — no region is assumed by default. Tell us the region you need and we'll confirm it in writing.",
    comfortTitle: "Comfort & display",
    accountTitle: "Account",
    signedInAs: "Signed in as",
    signInMethod: "Sign-in method",
    magicLink: "Magic link · no password",
    demoSession: "Demo session — no account signed in",
  },
  ms: {
    profileTitle: "Profil organisasi",
    nameLabel: "Nama organisasi",
    typeLabel: "Jenis organisasi",
    typeCentre: "Pusat kanak-kanak awal",
    typeClinic: "Klinik",
    typeSchool: "Sekolah",
    typeOther: "Lain-lain",
    profileSaved: "Profil disimpan.",
    saving: "Menyimpan…",
    residencyNotConfigurable:
      "Ini belum boleh ditetapkan dari portal. Pemastautinan data sebenar ditentukan oleh perjanjian pengehosan anda dengan kami — tiada wilayah diandaikan secara lalai. Beritahu kami wilayah yang anda perlukan dan kami akan mengesahkannya secara bertulis.",
    comfortTitle: "Keselesaan & paparan",
    accountTitle: "Akaun",
    signedInAs: "Dilog masuk sebagai",
    signInMethod: "Kaedah log masuk",
    magicLink: "Pautan ajaib · tiada kata laluan",
    demoSession: "Sesi demo — tiada akaun dilog masuk",
  },
} as const;

// ---- Types ------------------------------------------------------------------

const ORG_TYPE_VALUES: readonly OrganisationType[] = [
  "centre",
  "clinic",
  "school",
  "other",
];

/** `useOrg().orgType` is a plain string (it comes off the wire); narrow it. */
export function asOrgType(value: string): OrganisationType {
  return (ORG_TYPE_VALUES as readonly string[]).includes(value)
    ? (value as OrganisationType)
    : "other";
}

/**
 * Turns a failed `update_organisation` into calm, specific copy.
 *
 * Branches on the Postgres SQLSTATE carried by {@link PortalWriteError}, never
 * on message text — same contract as `createOrgErrorCopy` in
 * app/auth/CreateOrganisation.tsx. 42501 is the one that actually happens in
 * the field: you were demoted (or your session lapsed) while this tab was open.
 */
export function saveOrgErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  const msg = err instanceof Error ? err.message.toLowerCase() : "";

  if (code === "42501") {
    return "You no longer have admin rights on this organisation. Nothing was changed.";
  }
  if (code === "P0002") {
    return "We couldn't find this organisation any more. Try signing out and back in.";
  }
  if (code === "P0001") {
    if (msg.includes("name is required")) {
      return "Please give your organisation a name.";
    }
    return "We couldn't save that. Please check the details and try again.";
  }
  return "Something went wrong on our side. Nothing was changed — please try again in a moment.";
}

/** What the admin shell hands every screen through the Outlet. */
interface AdminOutletContext {
  email?: string;
  userId?: string;
  signOut?: () => void;
  orgName?: string;
}

// ---- Section card wrapper ---------------------------------------------------

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--sh-1)",
        padding: "20px 22px 22px",
      }}
    >
      {children}
    </div>
  );
}

// ---- Comfort toggle row -----------------------------------------------------

function ComfortToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 0",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <span style={{ fontSize: 14.5, color: "var(--ink)" }}>{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        style={{
          display: "inline-flex",
          width: 44,
          height: 26,
          borderRadius: 13,
          background: checked ? "var(--brand)" : "var(--line)",
          border: "none",
          position: "relative",
          transition: "background .15s",
          flexShrink: 0,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: checked ? 21 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,.15)",
            transition: "left .15s",
          }}
        />
        <span className="sr-only">{label}</span>
      </button>
    </div>
  );
}

// ---- Presentational view ----------------------------------------------------

export interface OrgSettingsViewProps {
  name: string;
  type: OrganisationType;
  onName: (value: string) => void;
  onType: (value: OrganisationType) => void;
  onSave: () => void;
  /** True while the write is in flight — the button must not claim success yet. */
  saving: boolean;
  /** True only AFTER the write resolved. */
  saved: boolean;
  /** Calm copy for a failed write. Empty when there is nothing wrong. */
  saveError: string;
  /** The signed-in adult's real email, from the Outlet context. */
  email?: string;
  onSignOut?: () => void;
}

export function OrgSettingsView({
  name,
  type,
  onName,
  onType,
  onSave,
  saving,
  saved,
  saveError,
  email,
  onSignOut,
}: OrgSettingsViewProps) {
  const { locale, calm, bigText, spacing, contrast, dark, toggle, setLocale } =
    useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.settings;
  const common = dict.common;
  const l = L[locale];

  const ORG_TYPES: Array<{ value: OrganisationType; label: string }> = [
    { value: "centre", label: l.typeCentre },
    { value: "clinic", label: l.typeClinic },
    { value: "school", label: l.typeSchool },
    { value: "other", label: l.typeOther },
  ];

  const INPUT_STYLE: React.CSSProperties = {
    height: 44,
    padding: "0 14px",
    border: "1.5px solid var(--line)",
    borderRadius: "var(--r-md)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-body)",
    fontSize: 14.5,
    outline: "none",
  };

  const SAVE_BTN: React.CSSProperties = {
    minHeight: 44,
    padding: "0 22px",
    border: 0,
    borderRadius: "var(--r-pill)",
    background: saving ? "var(--line-strong)" : "var(--brand)",
    color: "#fff",
    fontFamily: "var(--font-body)",
    fontWeight: 600,
    fontSize: 14.5,
    cursor: saving ? "default" : "pointer",
    boxShadow: "var(--sh-1)",
  };

  const LABEL_STYLE: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--ink-soft)",
  };

  return (
    <>
      {/* Page header */}
      <div>
        <h1
          style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0 }}
        >
          {t.title}
        </h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 16.5, margin: "8px 0 0" }}>
          {t.subtitle}
        </p>
      </div>

      {/* Org profile */}
      <div style={{ marginTop: 24 }}>
        <SectionCard>
          <h2
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 16px" }}
          >
            {l.profileTitle}
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label htmlFor="org-name" style={LABEL_STYLE}>
                {l.nameLabel}
              </label>
              <input
                id="org-name"
                type="text"
                value={name}
                disabled={saving}
                onChange={(e) => onName(e.target.value)}
                style={{ ...INPUT_STYLE, maxWidth: 420 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label htmlFor="org-type" style={LABEL_STYLE}>
                {l.typeLabel}
              </label>
              <select
                id="org-type"
                value={type}
                disabled={saving}
                onChange={(e) => onType(e.target.value as OrganisationType)}
                style={{ ...INPUT_STYLE, maxWidth: 260, cursor: "pointer" }}
              >
                {ORG_TYPES.map((ot) => (
                  <option key={ot.value} value={ot.value}>
                    {ot.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button onClick={onSave} disabled={saving} style={SAVE_BTN}>
              {saving ? l.saving : common.saveChanges}
            </button>

            {/* Announced, never red. Success appears only once the write has
                actually resolved — see OrgSettingsLive.handleSave. */}
            <div role="status" aria-live="polite">
              {saveError ? (
                <span
                  style={{
                    display: "inline-block",
                    padding: "9px 13px",
                    background: "var(--peach-tint)",
                    color: "var(--peach-deep)",
                    borderRadius: "var(--r-md)",
                    fontSize: 13.5,
                    lineHeight: 1.5,
                  }}
                >
                  {saveError}
                </span>
              ) : saved ? (
                <span style={{ fontSize: 13.5, color: "var(--sage-deep)" }}>
                  {l.profileSaved}
                </span>
              ) : null}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Data residency region — read-only on purpose. There is no store behind
          it, so it must not offer a Save it cannot honour. */}
      <div style={{ marginTop: 16 }}>
        <SectionCard>
          <h2
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 6px" }}
          >
            {t.residencyTitle}
          </h2>
          <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 14px" }}>
            {t.residencyBody}
          </p>

          <div
            style={{
              padding: "11px 15px",
              background: "var(--surface-sunk)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-md)",
              fontSize: 13.5,
              color: "var(--ink-soft)",
              lineHeight: 1.55,
            }}
          >
            {l.residencyNotConfigurable}
          </div>
        </SectionCard>
      </div>

      {/* Comfort modes */}
      <div style={{ marginTop: 16 }}>
        <SectionCard>
          <h2
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 4px" }}
          >
            {l.comfortTitle}
          </h2>
          <ComfortToggleRow
            label={dict.comfort.calmMode}
            checked={calm}
            onChange={() => toggle("calm")}
          />
          <ComfortToggleRow
            label={dict.comfort.biggerText}
            checked={bigText}
            onChange={() => toggle("bigText")}
          />
          <ComfortToggleRow
            label={dict.comfort.readingSpacing}
            checked={spacing}
            onChange={() => toggle("spacing")}
          />
          <ComfortToggleRow
            label={dict.comfort.higherContrast}
            checked={contrast}
            onChange={() => toggle("contrast")}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "12px 0",
            }}
          >
            <span style={{ fontSize: 14.5, color: "var(--ink)" }}>{dict.comfort.darkTheme}</span>
            <button
              role="switch"
              aria-checked={dark}
              onClick={() => toggle("dark")}
              style={{
                display: "inline-flex",
                width: 44,
                height: 26,
                borderRadius: 13,
                background: dark ? "var(--brand)" : "var(--line)",
                border: "none",
                position: "relative",
                transition: "background .15s",
                flexShrink: 0,
                cursor: "pointer",
                padding: 0,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: dark ? 21 : 3,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,.15)",
                  transition: "left .15s",
                }}
              />
              <span className="sr-only">{dict.comfort.darkTheme}</span>
            </button>
          </div>
        </SectionCard>
      </div>

      {/* Language */}
      <div style={{ marginTop: 16 }}>
        <SectionCard>
          <h2
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 6px" }}
          >
            {dict.language.title}
          </h2>
          <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 14px" }}>
            {dict.language.subtitle}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {LOCALES.map((loc) => (
              <button
                key={loc}
                onClick={() => setLocale(loc)}
                style={{
                  minHeight: 44,
                  padding: "0 18px",
                  borderRadius: "var(--r-pill)",
                  border:
                    locale === loc ? "2px solid var(--brand)" : "1.5px solid var(--line)",
                  background: locale === loc ? "var(--brand-tint)" : "transparent",
                  color: locale === loc ? "var(--brand)" : "var(--ink-soft)",
                  fontFamily: "var(--font-body)",
                  fontWeight: locale === loc ? 700 : 500,
                  fontSize: 14.5,
                  cursor: "pointer",
                }}
              >
                {LOCALE_LABEL[loc]}
              </button>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Account */}
      <div style={{ marginTop: 16 }}>
        <SectionCard>
          <h2
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 16px" }}
          >
            {l.accountTitle}
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span
                style={{ fontSize: 13.5, color: "var(--ink-soft)", minWidth: 130, flexShrink: 0 }}
              >
                {l.signedInAs}
              </span>
              <span style={{ fontSize: 14.5, color: "var(--ink-deep)", fontWeight: 500 }}>
                {email || l.demoSession}
              </span>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span
                style={{ fontSize: 13.5, color: "var(--ink-soft)", minWidth: 130, flexShrink: 0 }}
              >
                {l.signInMethod}
              </span>
              <span style={{ fontSize: 14.5, color: "var(--ink)" }}>{l.magicLink}</span>
            </div>
          </div>

          <button
            onClick={() => onSignOut?.()}
            style={{
              marginTop: 16,
              minHeight: 44,
              padding: "0 20px",
              border: "1.5px solid var(--line)",
              borderRadius: "var(--r-pill)",
              background: "transparent",
              color: "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            {common.signOut}
          </button>
        </SectionCard>
      </div>
    </>
  );
}

// ---- Dispatcher + demo/live wrappers ----------------------------------------

/** Real organisation writes when Supabase is configured, else the demo store. */
export function OrgSettings() {
  return shouldUseSupabase() ? <OrgSettingsLive /> : <OrgSettingsDemo />;
}

/**
 * Demo path. No backend, so the profile edit is local only — but the org it
 * shows is the one `useOrg()` hands the demo portal, not a fixture import, and
 * identity/sign-out still come from the shell.
 */
function OrgSettingsDemo() {
  const { orgName, orgType } = useOrg();
  const ctx = useOutletContext<AdminOutletContext | null>();
  const [name, setName] = useState(orgName);
  const [type, setType] = useState<OrganisationType>(() => asOrgType(orgType));
  const [saved, setSaved] = useState(false);

  return (
    <OrgSettingsView
      name={name}
      type={type}
      onName={(v) => {
        setName(v);
        setSaved(false);
      }}
      onType={(v) => {
        setType(v);
        setSaved(false);
      }}
      onSave={() => setSaved(true)}
      saving={false}
      saved={saved}
      saveError=""
      email={ctx?.email}
      onSignOut={ctx?.signOut}
    />
  );
}

/**
 * Live path.
 *
 * There is no fetch here: the admin gate resolves the organisation before the
 * shell renders, so `orgId` / `orgName` / `orgType` are already real by the time
 * this mounts (which is also why there is no loading or empty state to build —
 * an admin always has exactly one org, and `status === "admin"` guarantees it).
 * The only asynchrony is the write, and that is what the busy/success/error
 * states below cover.
 */
function OrgSettingsLive() {
  const { orgId, orgName, orgType, refresh } = useOrg();
  const ctx = useOutletContext<AdminOutletContext | null>();

  const [name, setName] = useState(orgName);
  const [type, setType] = useState<OrganisationType>(() => asOrgType(orgType));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Re-seed whenever the resolved organisation changes (first resolve, or a
  // refresh() landing after a save). Keyed on the values, so a user's in-flight
  // typing is only ever replaced when the org itself actually changed.
  useEffect(() => {
    setName(orgName);
    setType(asOrgType(orgType));
  }, [orgName, orgType]);

  async function handleSave() {
    if (saving) return;
    const trimmed = name.trim();
    setSaved(false);
    if (!trimmed) {
      setSaveError("Please give your organisation a name.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await getPortalData().updateOrganisation(orgId, trimmed, type);
      setName(trimmed);
      setSaved(true);
      // So the sidebar's organisation name follows the rename too.
      refresh();
    } catch (err) {
      setSaveError(saveOrgErrorCopy(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <OrgSettingsView
      name={name}
      type={type}
      onName={(v) => {
        setName(v);
        setSaved(false);
        setSaveError("");
      }}
      onType={(v) => {
        setType(v);
        setSaved(false);
        setSaveError("");
      }}
      onSave={() => void handleSave()}
      saving={saving}
      saved={saved}
      saveError={saveError}
      email={ctx?.email}
      onSignOut={ctx?.signOut}
    />
  );
}
